import React, { useEffect, useMemo, useState } from "react";
import { Search, Plus, Download, Eye, Pencil, Trash2, Undo2, X, PackageOpen } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, downloadFile } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel } from "../lib/ui";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const PURPOSES = ["Photoshoot", "Live", "Sample", "Lainnya"];
const num = (n) => (Number(n) || 0).toLocaleString("id-ID");
const today = () => new Date().toISOString().slice(0, 10);

const STATUS_STYLE = {
  Borrowed: "bg-amber-50 text-amber-700 border-amber-200",
  "Partially Returned": "bg-blue-50 text-blue-700 border-blue-200",
  Returned: "bg-green-50 text-green-700 border-green-200",
};
const STATUS_LABEL = { Borrowed: "Dipinjam", "Partially Returned": "Sebagian Kembali", Returned: "Dikembalikan" };

function StatusBadge({ status }) {
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${STATUS_STYLE[status] || ""}`} data-testid={`so-status-${status}`}>{STATUS_LABEL[status] || status}</span>;
}

export default function StockOut() {
  const { can } = useAuth();
  const canWrite = can("sales");
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [purpose, setPurpose] = useState("all");
  const [status, setStatus] = useState("all");
  const [mode, setMode] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [returnFor, setReturnFor] = useState(null);

  useEffect(() => { const t = setTimeout(() => { setDebounced(q); setPage(1); }, 350); return () => clearTimeout(t); }, [q]);

  const params = useMemo(() => ({
    q: debounced || undefined, purpose: purpose !== "all" ? purpose : undefined,
    status: status !== "all" ? status : undefined, mode: mode !== "all" ? mode : undefined,
    start: start || undefined, end: end || undefined, page, limit,
  }), [debounced, purpose, status, mode, start, end, page, limit]);

  const load = () => {
    setLoading(true);
    api.get("/stock-outs", { params }).then((r) => setData(r.data)).catch(() => toast.error("Gagal memuat")).finally(() => setLoading(false));
  };
  useEffect(load, [params]);

  const doExport = () => downloadFile("/stock-outs/export", { ...params, page: undefined, limit: undefined }, "stock_out.xlsx").catch(() => toast.error("Gagal export"));

  const doDelete = async (row) => {
    if (!window.confirm(`Hapus Stock Out ${row.number}? Stok yang dipinjam (bila mode kurangi stok) akan dikembalikan.`)) return;
    try { await api.delete(`/stock-outs/${row.id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const start0 = (data.page - 1) * limit;
  const itemsTotal = (r) => (r.items || []).reduce((s, i) => s + Number(i.qty || 0), 0);
  const itemsRet = (r) => (r.items || []).reduce((s, i) => s + Number(i.returned_qty || 0), 0);

  return (
    <div data-testid="stock-out-page">
      <PageHeader title="Stock Out" subtitle="Peminjaman stok (photoshoot, live, sample)">
        {canWrite && <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="so-create-btn"><Plus className="w-4 h-4 mr-1.5" />Create</Button>}
        <Button variant="outline" onClick={doExport} data-testid="so-export-btn"><Download className="w-4 h-4 mr-1.5" />Export</Button>
      </PageHeader>

      <Panel className="p-0">
        <div className="flex flex-wrap items-end gap-3 px-4 py-3 border-b border-taupe/15">
          <div className="relative w-56">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-wmuted" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari no / peminjam / produk..." className="pl-8 h-9" data-testid="so-search" />
          </div>
          <div><Label className="text-xs">Tujuan</Label>
            <Select value={purpose} onValueChange={(v) => { setPurpose(v); setPage(1); }}>
              <SelectTrigger className="h-9 w-36" data-testid="so-filter-purpose"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua Tujuan</SelectItem>{PURPOSES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Status</Label>
            <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
              <SelectTrigger className="h-9 w-40" data-testid="so-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua Status</SelectItem><SelectItem value="Borrowed">Dipinjam</SelectItem><SelectItem value="Partially Returned">Sebagian Kembali</SelectItem><SelectItem value="Returned">Dikembalikan</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Mode</Label>
            <Select value={mode} onValueChange={(v) => { setMode(v); setPage(1); }}>
              <SelectTrigger className="h-9 w-40" data-testid="so-filter-mode"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua Mode</SelectItem><SelectItem value="reduce">Kurangi Stok</SelectItem><SelectItem value="flag">Tanpa Kurangi</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Dari</Label><Input type="date" value={start} onChange={(e) => { setStart(e.target.value); setPage(1); }} className="h-9 w-40" data-testid="so-filter-start" /></div>
          <div><Label className="text-xs">Sampai</Label><Input type="date" value={end} onChange={(e) => { setEnd(e.target.value); setPage(1); }} className="h-9 w-40" data-testid="so-filter-end" /></div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">No</TableHead>
              <TableHead>No. Stock Out</TableHead>
              <TableHead>Peminjam</TableHead>
              <TableHead>Tujuan</TableHead>
              <TableHead>Mode</TableHead>
              <TableHead className="text-center">Items</TableHead>
              <TableHead>Tgl Pinjam</TableHead>
              <TableHead>Estimasi Kembali</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} className="text-center py-10 text-wmuted">Memuat...</TableCell></TableRow>
            ) : data.items.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-10 text-wmuted">Belum ada data Stock Out</TableCell></TableRow>
            ) : data.items.map((r, i) => (
              <TableRow key={r.id} data-testid={`so-row-${r.id}`}>
                <TableCell className="text-wmuted">{start0 + i + 1}</TableCell>
                <TableCell className="font-medium text-cocoa">{r.number}</TableCell>
                <TableCell>{r.borrower}</TableCell>
                <TableCell>{r.purpose}{r.purpose === "Lainnya" && r.purpose_note ? ` — ${r.purpose_note}` : ""}</TableCell>
                <TableCell><Badge variant="outline" className={r.mode === "reduce" ? "border-cocoa/30 text-cocoa" : "border-slate-300 text-slate-500"}>{r.mode === "reduce" ? "Kurangi Stok" : "Tanpa Kurangi"}</Badge></TableCell>
                <TableCell className="text-center">{(r.items || []).length} <span className="text-wmuted">({num(itemsRet(r))}/{num(itemsTotal(r))})</span></TableCell>
                <TableCell>{r.borrow_date}</TableCell>
                <TableCell>{r.expected_return_date || "-"}</TableCell>
                <TableCell><StatusBadge status={r.status} /></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDetail(r)} data-testid={`so-detail-${r.id}`}><Eye className="w-4 h-4" /></Button>
                  {canWrite && r.status !== "Returned" && <Button size="icon" variant="ghost" className="h-8 w-8 text-blue-600" onClick={() => setReturnFor(r)} data-testid={`so-return-${r.id}`}><Undo2 className="w-4 h-4" /></Button>}
                  {canWrite && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditing(r); setFormOpen(true); }} data-testid={`so-edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>}
                  {canWrite && <Button size="icon" variant="ghost" className="h-8 w-8 text-status-reject" onClick={() => doDelete(r)} data-testid={`so-delete-${r.id}`}><Trash2 className="w-4 h-4" /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-taupe/15 text-sm">
          <div className="flex items-center gap-2 text-wmuted">
            <span>Show</span>
            <Select value={String(limit)} onValueChange={(v) => { setLimit(Number(v)); setPage(1); }}>
              <SelectTrigger className="h-8 w-20" data-testid="so-limit"><SelectValue /></SelectTrigger>
              <SelectContent>{[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
            </Select>
            <span>entries · Menampilkan {data.total === 0 ? 0 : start0 + 1}–{Math.min(start0 + limit, data.total)} dari {data.total}</span>
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" disabled={data.page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="so-prev">Previous</Button>
            <span className="px-3 text-wmuted">Hal {data.page} / {data.pages}</span>
            <Button size="sm" variant="outline" disabled={data.page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="so-next">Next</Button>
          </div>
        </div>
      </Panel>

      {formOpen && <StockOutForm editing={editing} onClose={() => setFormOpen(false)} onSaved={() => { setFormOpen(false); load(); }} />}
      {detail && <DetailDialog row={detail} onClose={() => setDetail(null)} />}
      {returnFor && <ReturnDialog row={returnFor} onClose={() => setReturnFor(null)} onSaved={() => { setReturnFor(null); load(); }} />}
    </div>
  );
}

function StockOutForm({ editing, onClose, onSaved }) {
  const isEdit = !!editing;
  const [borrower, setBorrower] = useState(editing?.borrower || "");
  const [purpose, setPurpose] = useState(editing?.purpose || "Photoshoot");
  const [purposeNote, setPurposeNote] = useState(editing?.purpose_note || "");
  const [modeVal, setModeVal] = useState(editing?.mode || "reduce");
  const [borrowDate, setBorrowDate] = useState(editing?.borrow_date || today());
  const [expReturn, setExpReturn] = useState(editing?.expected_return_date || "");
  const [note, setNote] = useState(editing?.note || "");
  const [items, setItems] = useState(() => (editing?.items || []).map((i) => ({ ...i })));
  const [saving, setSaving] = useState(false);
  const hasReturns = (editing?.items || []).some((i) => Number(i.returned_qty || 0) > 0);
  const itemsLocked = isEdit && hasReturns;

  const [pq, setPq] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    if (itemsLocked) return;
    const t = setTimeout(() => {
      if (!pq || pq.length < 2) { setResults([]); return; }
      setSearching(true);
      api.get("/keep/products", { params: { q: pq } }).then((r) => setResults(r.data.products || [])).catch(() => {}).finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(t);
  }, [pq, itemsLocked]);

  const addVariant = (p, v) => {
    if (items.some((it) => it.variant_id === v.variant_id)) { toast.info("Varian sudah ada"); return; }
    const whs = v.warehouses || [];
    const wh = whs.find((w) => (w.available || 0) > 0) || whs[0] || {};
    setItems([...items, {
      id: `new-${v.variant_id}`, variant_id: v.variant_id, product_id: p.product_id,
      product_name: p.product_name, color: v.color, size: v.size,
      warehouse_id: wh.warehouse_id || "", warehouse_name: wh.warehouse_name || "",
      qty: 1, returned_qty: 0, _warehouses: v.warehouses || [], _borrowed_flag: v.borrowed_flag || 0,
    }]);
    setPq(""); setResults([]);
  };
  const setItem = (idx, patch) => setItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const save = async () => {
    if (!borrower.trim()) return toast.error("Nama peminjam wajib diisi");
    if (items.length === 0) return toast.error("Tambahkan minimal 1 item");
    const payload = {
      borrower, purpose, purpose_note: purposeNote, mode: modeVal,
      borrow_date: borrowDate, expected_return_date: expReturn, note,
      items: items.map((it) => ({ variant_id: it.variant_id, warehouse_id: it.warehouse_id, qty: Number(it.qty) })),
    };
    setSaving(true);
    try {
      if (isEdit) await api.put(`/stock-outs/${editing.id}`, payload);
      else await api.post("/stock-outs", payload);
      toast.success(isEdit ? "Stock Out diperbarui" : "Stock Out dibuat");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="so-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit Stock Out ${editing.number}` : "Create Stock Out"}</DialogTitle>
          <DialogDescription>Peminjaman stok untuk photoshoot, live, atau sample.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Peminjam / PIC *</Label><Input value={borrower} onChange={(e) => setBorrower(e.target.value)} placeholder="Nama peminjam" data-testid="so-borrower" /></div>
            <div><Label>Tujuan</Label>
              <Select value={purpose} onValueChange={setPurpose}>
                <SelectTrigger data-testid="so-purpose"><SelectValue /></SelectTrigger>
                <SelectContent>{PURPOSES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {purpose === "Lainnya" && <div className="md:col-span-2"><Label>Keterangan Tujuan</Label><Input value={purposeNote} onChange={(e) => setPurposeNote(e.target.value)} data-testid="so-purpose-note" /></div>}
            <div><Label>Tanggal Pinjam</Label><Input type="date" value={borrowDate} onChange={(e) => setBorrowDate(e.target.value)} data-testid="so-borrow-date" /></div>
            <div><Label>Estimasi Kembali</Label><Input type="date" value={expReturn} onChange={(e) => setExpReturn(e.target.value)} data-testid="so-exp-return" /></div>
          </div>

          <div>
            <Label>Mode Stok</Label>
            <div className="flex flex-col sm:flex-row gap-2 mt-1">
              <button type="button" disabled={isEdit} onClick={() => setModeVal("reduce")} data-testid="so-mode-reduce"
                className={`flex-1 text-left border rounded-lg p-3 ${modeVal === "reduce" ? "border-cocoa bg-cocoa/5" : "border-taupe/25"} ${isEdit ? "opacity-60 cursor-not-allowed" : ""}`}>
                <div className="font-semibold text-sm text-cocoa">Kurangi Stok</div>
                <div className="text-xs text-wmuted">Available gudang berkurang; kembali otomatis saat Return.</div>
              </button>
              <button type="button" disabled={isEdit} onClick={() => setModeVal("flag")} data-testid="so-mode-flag"
                className={`flex-1 text-left border rounded-lg p-3 ${modeVal === "flag" ? "border-cocoa bg-cocoa/5" : "border-taupe/25"} ${isEdit ? "opacity-60 cursor-not-allowed" : ""}`}>
                <div className="font-semibold text-sm text-cocoa">Tanpa Kurangi Stok</div>
                <div className="text-xs text-wmuted">Stok tetap, muncul penanda "sedang dipinjam" saat Keep / Sales.</div>
              </button>
            </div>
            {isEdit && <p className="text-xs text-wmuted mt-1">Mode tidak dapat diubah setelah dibuat.</p>}
          </div>

          <div>
            <Label>Item Dipinjam</Label>
            {!itemsLocked && (
              <div className="relative">
                <Input value={pq} onChange={(e) => setPq(e.target.value)} placeholder="Cari produk untuk ditambahkan (min 2 huruf)..." data-testid="so-item-search" />
                {(results.length > 0 || searching) && (
                  <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-taupe/25 rounded-lg shadow-lg">
                    {searching && <div className="px-3 py-2 text-sm text-wmuted">Mencari...</div>}
                    {results.map((p) => (p.variants || []).map((v) => (
                      <button type="button" key={v.variant_id} onClick={() => addVariant(p, v)} data-testid={`so-pick-${v.variant_id}`}
                        className="w-full text-left px-3 py-2 hover:bg-taupe/10 text-sm flex justify-between items-center">
                        <span>{p.product_name} <span className="text-wmuted">{v.color} {v.size}</span>{v.borrowed_flag ? <span className="ml-2 text-xs text-amber-600">• {v.borrowed_flag} dipinjam</span> : null}</span>
                        <span className="text-xs text-wmuted">Avail: {(v.warehouses || []).reduce((s, w) => s + (w.available || 0), 0)}</span>
                      </button>
                    )))}
                  </div>
                )}
              </div>
            )}
            {itemsLocked && <p className="text-xs text-amber-600 mt-1">Item terkunci karena sudah ada pengembalian.</p>}
            <div className="mt-2 border border-taupe/20 rounded-lg overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Produk</TableHead><TableHead>Gudang</TableHead><TableHead className="w-24">Qty</TableHead><TableHead className="w-10"></TableHead></TableRow></TableHeader>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center py-6 text-wmuted text-sm">Belum ada item</TableCell></TableRow>
                  ) : items.map((it, idx) => (
                    <TableRow key={it.id}>
                      <TableCell className="text-sm">{it.product_name} <span className="text-wmuted">{it.color} {it.size}</span></TableCell>
                      <TableCell>
                        {it._warehouses ? (
                          <Select value={it.warehouse_id} onValueChange={(v) => { const w = it._warehouses.find((x) => x.warehouse_id === v); setItem(idx, { warehouse_id: v, warehouse_name: w?.warehouse_name || "" }); }} disabled={itemsLocked}>
                            <SelectTrigger className="h-8 w-52" data-testid={`so-item-wh-${it.variant_id}`}><SelectValue placeholder="Pilih gudang" /></SelectTrigger>
                            <SelectContent>{it._warehouses.map((w) => <SelectItem key={w.warehouse_id} value={w.warehouse_id}>{w.warehouse_name} (avail {w.available})</SelectItem>)}</SelectContent>
                          </Select>
                        ) : <span className="text-sm">{it.warehouse_name}</span>}
                      </TableCell>
                      <TableCell><Input type="number" min={1} value={it.qty} onChange={(e) => setItem(idx, { qty: e.target.value })} className="h-8 w-20" disabled={itemsLocked} data-testid={`so-item-qty-${it.variant_id}`} /></TableCell>
                      <TableCell>{!itemsLocked && <Button size="icon" variant="ghost" className="h-7 w-7 text-status-reject" onClick={() => removeItem(idx)}><X className="w-4 h-4" /></Button>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <div><Label>Catatan</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan tambahan (opsional)" data-testid="so-note" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={saving} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="so-save-btn">{saving ? "Menyimpan..." : (isEdit ? "Update" : "Simpan")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailDialog({ row, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="so-detail-dialog">
        <DialogHeader>
          <DialogTitle>Detail Stock Out — {row.number}</DialogTitle>
          <DialogDescription>Rincian peminjaman stok.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div><span className="text-wmuted">Peminjam:</span> <b>{row.borrower}</b></div>
            <div><span className="text-wmuted">Tujuan:</span> {row.purpose}{row.purpose_note ? ` — ${row.purpose_note}` : ""}</div>
            <div><span className="text-wmuted">Mode:</span> {row.mode === "reduce" ? "Kurangi Stok" : "Tanpa Kurangi Stok"}</div>
            <div><span className="text-wmuted">Status:</span> <StatusBadge status={row.status} /></div>
            <div><span className="text-wmuted">Tgl Pinjam:</span> {row.borrow_date}</div>
            <div><span className="text-wmuted">Estimasi Kembali:</span> {row.expected_return_date || "-"}</div>
          </div>
          {row.note && <div><span className="text-wmuted">Catatan:</span> {row.note}</div>}
          <div className="border border-taupe/20 rounded-lg overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Produk</TableHead><TableHead>Gudang</TableHead><TableHead className="text-center">Qty</TableHead><TableHead className="text-center">Kembali</TableHead><TableHead className="text-center">Sisa</TableHead></TableRow></TableHeader>
              <TableBody>
                {(row.items || []).map((it) => (
                  <TableRow key={it.id}>
                    <TableCell className="text-sm">{it.product_name} <span className="text-wmuted">{it.color} {it.size}</span></TableCell>
                    <TableCell className="text-sm">{it.warehouse_name}</TableCell>
                    <TableCell className="text-center">{num(it.qty)}</TableCell>
                    <TableCell className="text-center">{num(it.returned_qty)}</TableCell>
                    <TableCell className="text-center font-semibold">{num(Number(it.qty) - Number(it.returned_qty))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="text-xs text-wmuted">Dibuat oleh {row.created_by} • {row.created_at?.slice(0, 16).replace("T", " ")}</div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Tutup</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReturnDialog({ row, onClose, onSaved }) {
  const [qtys, setQtys] = useState(() => Object.fromEntries((row.items || []).map((i) => [i.id, 0])));
  const [saving, setSaving] = useState(false);
  const remaining = (it) => Number(it.qty) - Number(it.returned_qty || 0);
  const setAll = () => setQtys(Object.fromEntries((row.items || []).map((i) => [i.id, remaining(i)])));

  const submit = async () => {
    const items = Object.entries(qtys).filter(([, q]) => Number(q) > 0).map(([item_id, q]) => ({ item_id, qty: Number(q) }));
    if (items.length === 0) return toast.error("Isi qty yang dikembalikan");
    setSaving(true);
    try { await api.post(`/stock-outs/${row.id}/return`, { items }); toast.success("Pengembalian dicatat"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="so-return-dialog">
        <DialogHeader>
          <DialogTitle>Pengembalian — {row.number}</DialogTitle>
          <DialogDescription>Catat qty barang yang dikembalikan (bisa sebagian).</DialogDescription>
        </DialogHeader>
        <div className="flex justify-end"><Button size="sm" variant="outline" onClick={setAll} data-testid="so-return-all"><PackageOpen className="w-4 h-4 mr-1.5" />Kembalikan Semua</Button></div>
        <div className="border border-taupe/20 rounded-lg overflow-hidden mt-2">
          <Table>
            <TableHeader><TableRow><TableHead>Produk</TableHead><TableHead className="text-center">Sisa</TableHead><TableHead className="w-28 text-center">Qty Kembali</TableHead></TableRow></TableHeader>
            <TableBody>
              {(row.items || []).map((it) => (
                <TableRow key={it.id}>
                  <TableCell className="text-sm">{it.product_name} <span className="text-wmuted">{it.color} {it.size}</span></TableCell>
                  <TableCell className="text-center">{remaining(it)}</TableCell>
                  <TableCell className="text-center">
                    <Input type="number" min={0} max={remaining(it)} value={qtys[it.id]} onChange={(e) => setQtys({ ...qtys, [it.id]: Math.max(0, Math.min(remaining(it), Number(e.target.value))) })} className="h-8 w-20 mx-auto" data-testid={`so-return-qty-${it.id}`} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={submit} disabled={saving} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="so-return-submit">{saving ? "Menyimpan..." : "Proses Pengembalian"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
